/**
 * View settings configuration and helpers.
 *
 * Two independent config sources:
 *   1. View preferences – per-user session settings stored in localStorage.
 *      Applied when the user is NOT in read-only mode.
 *   2. Read-only run config – per-run settings stored in Firestore
 *      (field `viewConfig` on the inspectionRun document).
 *      Applied when the user IS in read-only mode.
 *
 * `getEffectiveViewConfig` picks the right source based on the current mode
 * and applies hierarchical section-toggle logic before returning.
 */

import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { INSPECTIONRUN_COLLECTION_NAME } from '../browser/FbCol';
import { safeParseJson } from '@appesteem/aeshared-js/loadwatch';

// ---------------------------------------------------------------------------
// Capabilities schema (groups + defaults)
// ---------------------------------------------------------------------------

const GROUPS = [
  {
    id: 'video',
    label: 'Video',
    sectionId: 'videoSection',
    capabilityIds: ['videoSection', 'eventsSparkline', 'scenarios', 'clickInformationPanel', 'overlayMouseClick', 'overlayActiveWindow', 'overlayClickWindow', 'overlayElement', 'trimVideo', 'captureImage'],
  },
  {
    id: 'watchLogViewer',
    label: 'Watch log viewer',
    sectionId: 'watchLogViewerSection',
    capabilityIds: ['watchLogViewerSection', 'filters', 'fileInfo', 'certInfo', 'processInfo'],
  },
  {
    id: 'violations',
    label: 'Violations',
    sectionId: 'violationsSection',
    capabilityIds: ['violationsSection', 'requestRedoViolation', 'addEditDeleteViolations', 'exportReview', 'refreshAutoinspect', 'autoInspectPanel'],
  },
  {
    id: 'clickImages',
    label: 'Click images',
    sectionId: 'clickImagesSection',
    capabilityIds: ['clickImagesSection', 'annotationForm', 'showExistingAnnotations', 'refreshClickImages'],
  },
  {
    id: 'reports',
    label: 'Reports',
    sectionId: 'reportsSection',
    capabilityIds: ['reportsSection', 'pdfDownload'],
  },
];

// Labels for each capability (shared by both modes)
const CAPABILITIES = {
  videoSection:             { label: 'Show video' },
  eventsSparkline:          { label: 'Events sparkline' },
  scenarios:                { label: 'Scenarios' },
  clickInformationPanel:    { label: 'Click information panel' },
  overlayMouseClick:        { label: 'Overlay: mouse click indicator' },
  overlayActiveWindow:      { label: 'Overlay: active window border' },
  overlayClickWindow:       { label: 'Overlay: click window border' },
  overlayElement:           { label: 'Overlay: element bounding rectangle' },
  trimVideo:                { label: 'Trim video' },
  captureImage:             { label: 'Capture image' },
  watchLogViewerSection:    { label: 'Show watch log viewer' },
  filters:                  { label: 'Filters' },
  fileInfo:                 { label: 'File info' },
  certInfo:                 { label: 'Cert info' },
  processInfo:              { label: 'Process info' },
  violationsSection:        { label: 'Show violations' },
  requestRedoViolation:     { label: 'Request / redo violation run' },
  addEditDeleteViolations:  { label: 'Add, edit, and delete violations' },
  exportReview:             { label: 'Export review' },
  refreshAutoinspect:       { label: 'Refresh autoinspect data' },
  autoInspectPanel:         { label: 'AutoInspect / violations panel' },
  clickImagesSection:       { label: 'Show click images' },
  annotationForm:           { label: 'Add, edit, and delete annotations' },
  showExistingAnnotations:  { label: 'View existing annotations' },
  refreshClickImages:       { label: 'Refresh click images' },
  reportsSection:           { label: 'Show reports' },
  pdfDownload:              { label: 'Download PDF report' },
};

// View preferences defaults: everything ON – users see all features unless they opt out
const VIEW_DEFAULTS = Object.fromEntries(Object.keys(CAPABILITIES).map((id) => [id, true]));

// Read-only defaults: viewing features ON, but mutation/editing capabilities OFF
const READONLY_DEFAULTS = {
  ...VIEW_DEFAULTS,
  trimVideo:                false,
  captureImage:             false,
  requestRedoViolation:     false,
  addEditDeleteViolations:  false,
  annotationForm:           false,
};

const LOCAL_STORAGE_VIEW_PREFS = 'aeinspect.viewPreferences';

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Merge user overrides with a given defaults map.
 * @param {object|null} overrides  User-provided boolean overrides (or null)
 * @param {{ [key: string]: boolean }} defaults  The defaults map to use
 * @returns {{ [key: string]: boolean }}
 */
function mergeWithDefaults(overrides, defaults) {
  const out = {};
  Object.keys(defaults).forEach((id) => {
    out[id] = overrides && typeof overrides[id] === 'boolean' ? overrides[id] : defaults[id];
  });
  return out;
}

// ---------------------------------------------------------------------------
// View preferences (localStorage – per-user session)
// ---------------------------------------------------------------------------

/**
 * Get view preferences (what the current user sees in normal mode).
 * @returns {{ [key: string]: boolean }}
 */
export function getViewPreferences() {
  let overrides = null;
  try {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_VIEW_PREFS);
      const parsed = raw ? safeParseJson(raw) : null;
      if (parsed && typeof parsed === 'object') overrides = parsed;
    }
  } catch (e) {
    // localStorage access can throw in Safari private mode etc.
    console.warn('readOnlyConfig: localStorage access failed', e);
  }
  return mergeWithDefaults(overrides, VIEW_DEFAULTS);
}

/**
 * Save view preferences (session, localStorage).
 * Only stores overrides that differ from the view defaults.
 * @param {{ [key: string]: boolean }} config
 */
export function setViewPreferences(config) {
  const overrides = {};
  Object.keys(VIEW_DEFAULTS).forEach((id) => {
    const newValue = !!config[id];
    if (newValue !== VIEW_DEFAULTS[id]) overrides[id] = newValue;
  });
  try {
    if (typeof window === 'undefined') return;
    if (Object.keys(overrides).length === 0) {
      window.localStorage.removeItem(LOCAL_STORAGE_VIEW_PREFS);
    } else {
      window.localStorage.setItem(LOCAL_STORAGE_VIEW_PREFS, JSON.stringify(overrides));
    }
  } catch (e) {
    console.warn('readOnlyConfig: could not write view preferences', e);
  }
}

// ---------------------------------------------------------------------------
// Read-only run config (Firestore – per-run)
// ---------------------------------------------------------------------------

/**
 * Load per-run view config from Firestore.
 * Reads the `viewConfig` field on the inspectionRun document.
 * @param {string} runId
 * @returns {Promise<{ [key: string]: boolean } | null>} merged config or null
 */
export async function loadRunViewConfig(runId) {
  if (!runId) return null;
  try {
    const docRef = doc(db, INSPECTIONRUN_COLLECTION_NAME, runId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = docSnap.data();
    if (!data.viewConfig || typeof data.viewConfig !== 'object') return null;
    return mergeWithDefaults(data.viewConfig, READONLY_DEFAULTS);
  } catch (e) {
    console.warn('readOnlyConfig: could not load run view config', e);
    return null;
  }
}

/**
 * Save per-run view config to Firestore.
 * Stores only overrides (like localStorage) in the `viewConfig` field.
 * @param {string} runId
 * @param {{ [key: string]: boolean }} config
 */
export async function saveRunViewConfig(runId, config) {
  if (!runId) return;
  const overrides = {};
  Object.keys(READONLY_DEFAULTS).forEach((id) => {
    const newValue = !!config[id];
    if (newValue !== READONLY_DEFAULTS[id]) overrides[id] = newValue;
  });
  try {
    const docRef = doc(db, INSPECTIONRUN_COLLECTION_NAME, runId);
    await updateDoc(docRef, { viewConfig: overrides });
  } catch (e) {
    console.error('readOnlyConfig: could not save run view config', e);
  }
}

// ---------------------------------------------------------------------------
// Section-toggle logic
// ---------------------------------------------------------------------------

/**
 * Build map: capabilityId -> sectionId for groups that have a sectionId.
 * When the section is off, all capabilities in that group are effectively off.
 */
function getCapabilityToSectionMap() {
  const map = {};
  GROUPS.forEach((g) => {
    const sectionId = g.sectionId;
    if (sectionId && Array.isArray(g.capabilityIds)) {
      g.capabilityIds.forEach((id) => {
        map[id] = sectionId;
      });
    }
  });
  return map;
}

/**
 * Apply section-toggle logic: when a section is off, all capabilities in that
 * section are forced to false.
 * @param {{ [key: string]: boolean }} config Raw config
 * @returns {{ [key: string]: boolean }}
 */
export function getEffectiveReadOnlyConfig(config) {
  const capToSection = getCapabilityToSectionMap();
  const out = {};
  Object.keys(CAPABILITIES).forEach((id) => {
    const sectionId = capToSection[id];
    const sectionOn = sectionId ? !!config[sectionId] : true;
    out[id] = sectionOn && !!config[id];
  });
  return out;
}

// ---------------------------------------------------------------------------
// Effective config (mode-aware)
// ---------------------------------------------------------------------------

/**
 * Default read-only capability values (no overrides).
 * Mutation/editing capabilities are off by default.
 * @returns {{ [key: string]: boolean }}
 */
export function getDefaultReadOnlyConfig() {
  return { ...READONLY_DEFAULTS };
}

/**
 * Default view preference values (no overrides).
 * Everything is on so users see all features.
 * @returns {{ [key: string]: boolean }}
 */
export function getDefaultViewConfig() {
  return { ...VIEW_DEFAULTS };
}

/**
 * Compute the effective view config to apply.
 *
 * - Normal mode (`readOnly` false): uses view preferences from localStorage.
 * - Read-only mode (`readOnly` true): uses `readOnlyRunConfig` (loaded from
 *   Firestore); falls back to defaults when no Firestore data exists.
 *
 * In both cases the section-toggle logic is applied before returning.
 *
 * @param {{ readOnly?: boolean, readOnlyRunConfig?: { [key: string]: boolean } | null }} options
 * @returns {{ [key: string]: boolean }}
 */
export function getEffectiveViewConfig({ readOnly = false, readOnlyRunConfig = null } = {}) {
  const raw = readOnly
    ? (readOnlyRunConfig || getDefaultReadOnlyConfig())
    : getViewPreferences();
  return getEffectiveReadOnlyConfig(raw);
}

// ---------------------------------------------------------------------------
// Dialog helpers
// ---------------------------------------------------------------------------

/**
 * Get capabilities grouped for the config dialog (hierarchical).
 * @returns {{ groups: { id: string, label: string, sectionId?: string, capabilities: { id: string, label: string, isSectionToggle?: boolean }[] }[] }}
 */
export function getReadOnlyCapabilitiesTree() {
  return {
    groups: GROUPS.map((g) => ({
      id: g.id || 'group',
      label: g.label || 'Options',
      sectionId: g.sectionId || null,
      capabilities: (g.capabilityIds || [])
        .filter((id) => CAPABILITIES[id])
        .map((id) => ({
          id,
          label: CAPABILITIES[id]?.label || id,
          isSectionToggle: id === g.sectionId,
        })),
    })).filter((g) => g.capabilities.length > 0),
  };
}
